import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Sparkles, Heart, ArrowRight, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useAuth, routeAfterAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";

const LOGO = `${process.env.REACT_APP_BACKEND_URL}/api/brand/logo.png`;

const TIERS = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    sub: "+ $9.99 one-time setup",
    cta: "Start Free",
    features: ["Basic wedding website", "RSVP manager (up to 50 guests)", "10 photos", "1 invitation type"],
  },
  {
    id: "premium",
    name: "Premium",
    price: "$19",
    sub: "/month",
    cta: "Choose Premium",
    featured: true,
    features: ["Unlimited guests", "Unlimited memories", "All invitations", "Dress & Tux + Budget"],
  },
  {
    id: "luxury",
    name: "Luxury",
    price: "$49",
    sub: "/month",
    cta: "Choose Luxury",
    features: ["Everything in Premium", "Custom domain", "AI Vow Studio + Concierge", "Live broadcasts"],
  },
  {
    id: "pro",
    name: "Planner Pro",
    price: "$99",
    sub: "/month",
    cta: "Choose Pro",
    features: ["White-label sites", "Unlimited clients", "Vendor marketplace", "Affiliate program"],
  },
];

// Planners & vendors see business plans only.
const PRO_TIERS = [
  { id: "free", name: "Starter", price: "$0", sub: "+ $9.99 one-time setup", cta: "Start Free", features: ["Community profile & directory listing", "Client book with bulk upload", "Business reports", "Direct messages"] },
  { ...TIERS[3], featured: true, features: ["Everything in Starter", "White-label client sites", "Unlimited clients", "Vendor marketplace + affiliate program"] },
];

export default function ChoosePlanPage() {
  const { user, logout, setUser, loading } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(null);
  const [vendorTiers, setVendorTiers] = useState(null);
  useEffect(() => {
    api.get("/public/vendor-plans").then((r) => {
      const rows = (r.data || []).map((p, i, all) => ({ id: p.plan_id, name: p.name, price: p.monthly ? `$${p.monthly}` : "$0", sub: p.monthly ? "/month" : "+ $9.99 one-time setup",
        cta: p.monthly ? `Choose ${p.name}` : "Start Free", featured: p.plan_id === "pro" || (all.length === 1 && i === 0), features: p.features || [], free: !p.monthly }));
      if (rows.length) setVendorTiers(rows);
    }).catch(() => {});
  }, []);

  // Send already-onboarded users to their proper place
  useEffect(() => {
    if (loading) return;
    if (!user) {
      nav("/login", { replace: true });
      return;
    }
    if (user.plan_selected) {
      nav(routeAfterAuth(user), { replace: true });
    }
  }, [user, loading, nav]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-warm)]">
        <p className="text-[var(--muted)]">Loading…</p>
      </div>
    );
  }

  async function pick(tier) {
    if (!user) return;
    // Vendors/planners don't have a couple wedding to create, so route them
    // straight to the dashboard after plan selection. Couples go to onboarding.
    const postPlanRoute = !user.setup_fee_paid ? "/setup-fee" : (user.role === "vendor" || user.role === "planner") ? "/dashboard" : "/onboarding";
    if (tier.id === "free" || tier.free) {
      setBusy("free");
      try {
        const { data } = await api.post("/me/select-plan", { plan: "free" });
        setUser(data);
        toast.success("Free plan activated");
        nav(postPlanRoute);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setBusy(null); }
      return;
    }
    // Paid plans go to Stripe checkout; success_url redirects back and
    // the polling endpoint sets plan_selected_at server-side.
    setBusy(tier.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        plan_id: tier.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(null); }
  }

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-warm)]">
        <p className="text-[var(--muted)]">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-warm)]" data-testid="choose-plan-page">
      <header className="bg-[var(--bg)] border-b border-[var(--line)]">
        <div className="container-wide px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><img src={LOGO} alt="LockedInForever" className="h-12 md:h-14 w-auto" /></Link>
          <button onClick={logout} className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)] flex items-center gap-1 hover:text-[var(--ink)]" data-testid="choose-plan-logout">
            <LogOut className="w-3 h-3" /> Sign out
          </button>
        </div>
      </header>

      <section className="container-wide max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="text-center mb-10">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-3">Step 1 of 2 · Membership</p>
          <h1 className="font-serif font-light text-4xl md:text-5xl">Welcome, {user.name?.split(" ")[0] || "friend"}.</h1>
          <p className="font-script text-[var(--gold)] text-3xl md:text-4xl mt-2">Pick your plan to begin.</p>
          <p className="text-[var(--muted)] mt-4 max-w-xl mx-auto">Upgrade any time. Your data stays yours forever.</p>
          <p className="text-xs text-[#8a6d35] mt-2" data-testid="setup-fee-note">Every account includes a one-time $9.99 setup fee (added to your first payment, or paid on the next step for free plans).</p>
          <div className="mt-5 inline-flex items-center gap-2 bg-white border border-[var(--line)] rounded-full px-4 py-2 text-xs">
            <Heart className="w-3 h-3 text-[var(--gold)]" />
            <span><strong>Lifetime memories</strong> on every paid tier</span>
          </div>
        </div>

        <div className={`grid md:grid-cols-2 gap-5 ${["vendor", "planner"].includes(user?.role) ? ((vendorTiers || PRO_TIERS).length > 2 ? "lg:grid-cols-3 xl:grid-cols-5" : "max-w-3xl mx-auto") : "lg:grid-cols-4"}`}>
          {(["vendor", "planner"].includes(user?.role) ? (vendorTiers || PRO_TIERS) : TIERS).map((t) => (
            <div key={t.id} className={`bg-white border rounded-3xl p-6 flex flex-col ${t.featured ? "border-[var(--gold)] ring-2 ring-[#C8A96B]/20" : "border-[var(--line)]"}`} data-testid={`plan-${t.id}`}>
              {t.featured && <div className="mb-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-[var(--gold)]"><Sparkles className="w-3 h-3" /> Most Loved</div>}
              <h3 className="font-serif text-2xl mb-1">{t.name}</h3>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-serif text-4xl">{t.price}</span>
                <span className="text-xs text-[var(--muted)]">{t.sub}</span>
              </div>
              <ul className="space-y-2 my-5 text-sm flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0 text-[var(--gold)]" />{f}</li>
                ))}
              </ul>
              <button
                onClick={() => pick(t)}
                disabled={busy === t.id}
                data-testid={`select-${t.id}`}
                className={`${t.featured ? "btn-gold" : "btn-outline-gold"} w-full justify-center`}
              >
                {busy === t.id ? "…" : t.cta}
                {t.featured && busy !== t.id && <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-[var(--muted)] mt-10">
          Need a different fit? <Link to="/pricing" className="text-[var(--gold)] underline">See full pricing</Link>
        </p>
      </section>
    </div>
  );
}
